import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const defaultSEO = {
  title: 'AIVION | Intelligent AI Solutions for Meaningful Outcomes',
  description: 'Delivering meaningful outcomes through intelligent AI solutions.'
};

const pageSEO = {
  '/data-engineering': ['Data Engineering Services | AIVION', 'Modern data platforms, pipelines, and foundations for AI-ready enterprises.'],
  '/data-science': ['Data Science Services | AIVION', 'Production-grade data science, machine learning, and predictive analytics for better decisions.'],
  '/genai': ['Generative AI Solutions | AIVION', 'Enterprise generative AI solutions designed for practical, secure, and measurable business value.'],
  '/automation': ['AI Automation Services | AIVION', 'AI-augmented automation that streamlines operations and improves enterprise workflows.'],
  '/aivionscore': ['aivionScore | AI-Based Credit Review | AIVION', 'AI-powered credit review, risk scoring, and portfolio intelligence for informed decisions.'],
  '/aivionsales': ['aivionSales | AI-Based Sales and CRM | AIVION', 'Predictive sales analytics and AI automation for stronger pipelines and more accurate forecasting.'],
  '/aivionreach': ['aivionReach | AI-Supported Employment Platform | AIVION', 'An AI-supported employment platform helping people build skills, prepare applications, and find opportunities.'],
  '/digitaldeputee': ['DigitalDeputee | Digital Employees Platform | AIVION', 'Design, deploy, and govern AI agents that orchestrate enterprise workflows securely.'],
  '/financial-services': ['AI for Financial Services | AIVION', 'Trustworthy AI solutions for banking, lending, insurance, and financial services.'],
  '/education': ['AI for Education | AIVION', 'AI solutions for assessment, student analytics, and adaptive learning in education.'],
  '/healthcare': ['AI for Healthcare | AIVION', 'Trustworthy AI for clinical analytics, patient pathways, and healthcare operations.'],
  '/energy-utilities': ['AI for Energy and Utilities | AIVION', 'AI for forecasting, predictive maintenance, grid optimisation, and the energy transition.'],
  '/credit-risk': ['AI Credit Risk Assessment Case Study | AIVION', 'How AIVION improves credit risk assessment with AI-powered analysis and decision support.'],
  '/exam-assessment': ['AI Exam Assessment Case Study | AIVION', 'How AIVION enables consistent, scalable assessment with AI-assisted marking.'],
  '/blood-bank-management': ['Clinical Data Platform Case Study | AIVION', 'A clinical data platform improving patient outcomes and operational intelligence.'],
  '/fact-checking-reports': ['Predictive Maintenance Case Study | AIVION', 'Predictive maintenance and forecasting for reliable grid operations.'],
  '/financial-crime-monitoring': ['Financial Crime Monitoring Case Study | AIVION', 'Reducing false positives and improving financial crime monitoring with intelligent AI.'],
  '/genai-tutor': ['GenAI Tutor Case Study | AIVION', 'A generative AI tutor supporting online learning and personalised guidance.'],
  '/theatre-optimisation': ['Theatre Optimisation Case Study | AIVION', 'Using data and AI to improve elective recovery and theatre utilisation.'],
  '/generation-forecasting': ['Renewable Generation Forecasting Case Study | AIVION', 'Short-term generation forecasting for renewable energy trading and operations.']
};

const SEO = () => {
  const { pathname } = useLocation();
  const normalizedPath = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;

  useEffect(() => {
    const isKnownPage = normalizedPath === '/' || Object.prototype.hasOwnProperty.call(pageSEO, normalizedPath);
    const [title, description] = isKnownPage
      ? (pageSEO[normalizedPath] || [defaultSEO.title, defaultSEO.description])
      : ['Page not found | AIVION', 'The page you requested could not be found.'];
    document.title = title;

    const setMeta = (name, content) => {
      let element = document.querySelector(`meta[name="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('name', name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    setMeta('description', description);
    setMeta('robots', isKnownPage ? 'index, follow, max-image-preview:large' : 'noindex, follow');

    const setProperty = (property, content) => {
      let element = document.querySelector(`meta[property="${property}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const canonicalUrl = `https://www.aivion.ai${normalizedPath === '/' ? '/' : normalizedPath}`;
    setProperty('og:title', title);
    setProperty('og:description', description);
    setProperty('og:url', canonicalUrl);
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    setMeta('twitter:image', 'https://www.aivion.ai/images/og-image.svg');
    setProperty('og:image', 'https://www.aivion.ai/images/og-image.svg');
    setProperty('og:image:alt', 'AIVION — delivering meaningful outcomes through intelligent AI solutions');

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);
  }, [normalizedPath]);

  return null;
};

export default SEO;
